import { useEffect, useMemo, useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { ChevronDown, ChevronRight, MoreHorizontal } from 'lucide-react'
import { showErrorToast, showSuccessToast } from '@/lib/toast'
import { ApiError } from '@/lib/api/client'
import { changeUserRole, createInvitation, revokeInvitation } from '@/lib/api/admin'
import { addEmployeeSkill, removeEmployeeSkill } from '@/lib/api/v2/planning'
import {
  assignCrewMember,
  createCrew,
  deleteCrew,
  getCrews,
  unassignCrewMember,
  type CrewDto,
} from '@/lib/api/v2/crews'
import {
  createTeamMember,
  deactivateTeamMember,
  deleteTeamMember,
  getTeam,
  reactivateTeamMember,
  revokeTeamMemberAccess,
  updateTeamMember,
  type TeamFunction,
  type TeamMemberDto,
} from '@/lib/api/v2/team'
import type { AdminRole } from '@/components/features/admin/RolesPermissionsTab'
import {
  ALL_FUNCTIONS,
  crewIdByUser,
  filterMembers,
  formatDeactivatedAt,
  functionLabel,
  getAccessBadge,
  showCrewColumn,
  showFunctionsColumn,
  sortCrews,
  splitByActivity,
} from './team-helpers'

/**
 * SOLY-TEAM-001 — the Équipe tab: a person first (name + field functions),
 * access as an optional attribute granted/taken later. Replaces the old
 * Utilisateurs tab and the orphan employee-skills page.
 */

/**
 * Mirror of `PlanningCrew.MaxNameLength`. Named rather than inlined so the drift is visible: raise
 * the domain constant and the input keeps silently truncating, lower it and the user is refused
 * after typing. The refusal message states the number for the same reason.
 */
const CREW_NAME_MAX = 80

const ERROR_MESSAGES: Record<string, string> = {
  LAST_ADMIN: "Impossible : c'est le dernier administrateur actif",
  CANNOT_REMOVE_SELF: 'Vous ne pouvez pas agir sur votre propre accès',
  EMAIL_TAKEN: 'Cet email est déjà utilisé',
  ALREADY_ACTIVE: 'Ce membre a déjà un accès actif',
  ALREADY_MEMBER: 'Cet email appartient déjà à un membre',
  HAS_HISTORY: 'Cet employé a un historique — désactivez-le plutôt',
  HAS_ACCESS: "L'email d'un membre avec accès se gère via son compte",
  INVITATION_PENDING: "Révoquez l'invitation en cours avant de changer l'email",
  INVITATION_EMAIL_PENDING: 'Une invitation est déjà en cours pour cet email',
  MEMBER_DEACTIVATED: "Ce membre est désactivé — réactivez-le d'abord",
  CREW_NAME_TAKEN: 'Une équipe porte déjà ce nom',
  CREW_NAME_REQUIRED: "Le nom de l'équipe est obligatoire",
  CREW_NAME_TOO_LONG: `Le nom de l'équipe ne peut pas dépasser ${CREW_NAME_MAX} caractères`,
  CREW_NOT_FOUND: 'Équipe introuvable — rechargez la page',
  ALREADY_IN_CREW: "Ce membre vient d'être placé dans une équipe — rechargez la page",
  USER_NOT_FOUND: "Ce membre ne fait plus partie de l'organisation — rechargez la page",
}

function extractErrorCode(e: unknown): string | undefined {
  if (e instanceof ApiError) {
    const data = (
      e.response as
        | { data?: { error?: { code?: string }; errorCode?: string; ErrorCode?: string } }
        | undefined
    )?.data
    return data?.error?.code ?? data?.errorCode ?? data?.ErrorCode
  }
  return undefined
}

function toastApiError(e: unknown, fallback: string) {
  console.error(e)
  const code = extractErrorCode(e)
  showErrorToast((code && ERROR_MESSAGES[code]) || fallback)
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/** Sentinel values of the crew select — a Select item cannot carry an empty value. */
const CREW_NONE = '__none__'
const CREW_NEW = '__new__'

type ConfirmAction =
  | { kind: 'revoke-access'; member: TeamMemberDto }
  | { kind: 'deactivate'; member: TeamMemberDto }
  | { kind: 'delete'; member: TeamMemberDto }
  | { kind: 'revoke-invitation'; member: TeamMemberDto }

interface TeamTabProps {
  roles: AdminRole[]
  rolesLoading: boolean
  planningEnabled: boolean | undefined
  onCountChange?: (count: number) => void
}

export function TeamTab({ roles, rolesLoading, planningEnabled, onCountChange }: TeamTabProps) {
  const [members, setMembers] = useState<TeamMemberDto[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [inactiveOpen, setInactiveOpen] = useState(false)

  const showFunctions = showFunctionsColumn(planningEnabled)

  // SOLY-PLAN-005 — crews. Configuration of an agenda LENS: it groups lanes and changes nothing
  // else, so it lives as one column in the roster the admin already knows, not as a new screen.
  const showCrews = showCrewColumn(planningEnabled)
  const [crews, setCrews] = useState<CrewDto[]>([])
  const [crewTarget, setCrewTarget] = useState<TeamMemberDto | null>(null)
  const [newCrewName, setNewCrewName] = useState('')
  const [isSavingCrew, setIsSavingCrew] = useState(false)
  const [manageCrewsOpen, setManageCrewsOpen] = useState(false)
  const [crewToDelete, setCrewToDelete] = useState<CrewDto | null>(null)
  // Whether the crew list ACTUALLY arrived. Without it, a failed load left `crews` empty and every
  // select fell back to « Aucune équipe » — the screen asserting that nobody is in a crew, which is
  // a different claim from « je n'ai pas pu charger les équipes », and one the agenda next door
  // contradicts by grouping the very rows this page says do not exist.
  const [crewsLoaded, setCrewsLoaded] = useState(false)
  // One in-flight change per member: two quick picks on the same row put two PUTs in flight, the
  // second racing the first into the unique index. Same guard as `busyFunctions` on the Fonctions
  // column, added for exactly this.
  const [busyCrewFor, setBusyCrewFor] = useState<Set<string>>(new Set())
  const crewByUser = useMemo(() => crewIdByUser(crews), [crews])
  const orderedCrews = useMemo(() => sortCrews(crews), [crews])

  // Add dialog
  const [addOpen, setAddOpen] = useState(false)
  const [addFirstName, setAddFirstName] = useState('')
  const [addLastName, setAddLastName] = useState('')
  const [addEmail, setAddEmail] = useState('')
  const [addFunctions, setAddFunctions] = useState<TeamFunction[]>([])
  const [addWithInvite, setAddWithInvite] = useState(false)
  const [addRoleName, setAddRoleName] = useState('')
  const [isAdding, setIsAdding] = useState(false)

  // Edit dialog
  const [editTarget, setEditTarget] = useState<TeamMemberDto | null>(null)
  const [editFirstName, setEditFirstName] = useState('')
  const [editLastName, setEditLastName] = useState('')
  const [editEmail, setEditEmail] = useState('')
  const [editFunctions, setEditFunctions] = useState<TeamFunction[]>([])
  const [isEditing, setIsEditing] = useState(false)

  // Invite dialog (deferred invitation on an existing member)
  const [inviteTarget, setInviteTarget] = useState<TeamMemberDto | null>(null)
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteRoleName, setInviteRoleName] = useState('')
  const [isInviting, setIsInviting] = useState(false)

  // Change access role dialog
  const [roleTarget, setRoleTarget] = useState<TeamMemberDto | null>(null)
  const [roleName, setRoleName] = useState('')
  const [isChangingRole, setIsChangingRole] = useState(false)

  // Shared confirmation dialog for the destructive-ish lifecycle actions
  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null)
  const [isConfirming, setIsConfirming] = useState(false)

  const reload = async () => {
    try {
      setIsLoading(true)
      const resp = await getTeam()
      setMembers(resp.members)
      // Active team only — a deactivated member "n'apparaît plus dans l'équipe".
      onCountChange?.(resp.members.filter((m) => m.userId !== null && m.isActive).length)
    } catch (e) {
      console.error(e)
      showErrorToast("Impossible de charger l'équipe")
    } finally {
      setIsLoading(false)
    }
  }

  // Crews are loaded apart from the roster, and their failure is reported apart: the roster is this
  // page, the crews are one optional column on it. A crew list that will not load must not leave the
  // team invisible — nor fail in silence, which is the trap SOLY-REGIE-006 closed elsewhere.
  const reloadCrews = async () => {
    if (!showCrews) return
    try {
      setCrews(await getCrews())
      setCrewsLoaded(true)
    } catch (e) {
      console.error(e)
      setCrewsLoaded(false)
      showErrorToast('Impossible de charger les équipes')
    }
  }

  useEffect(() => {
    reload()
    reloadCrews()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const filtered = useMemo(() => filterMembers(members, search), [members, search])
  const { active, inactive } = useMemo(() => splitByActivity(filtered), [filtered])

  const defaultRoleName = roles[0]?.id ?? ''

  // One in-flight toggle per (member, function): a double click must not race
  // add/remove calls (server order not guaranteed) nor read a stale row.
  const [busyFunctions, setBusyFunctions] = useState<Set<string>>(new Set())

  const toggleFunction = async (member: TeamMemberDto, fn: TeamFunction) => {
    if (!member.userId) return
    const busyKey = `${member.userId}-${fn}`
    if (busyFunctions.has(busyKey)) return
    setBusyFunctions((prev) => new Set(prev).add(busyKey))

    const has = member.functions.includes(fn)
    // Optimistic toggle, resync on failure (pattern from the old skills page).
    setMembers((prev) =>
      prev.map((m) =>
        m.userId === member.userId
          ? {
              ...m,
              functions: has
                ? m.functions.filter((f) => f !== fn)
                : [...m.functions, fn].sort(),
            }
          : m,
      ),
    )
    try {
      if (has) {
        await removeEmployeeSkill(member.userId, fn)
      } else {
        await addEmployeeSkill(member.userId, fn)
      }
    } catch (e) {
      toastApiError(e, 'Erreur lors de la modification des fonctions')
      await reload()
    } finally {
      setBusyFunctions((prev) => {
        const next = new Set(prev)
        next.delete(busyKey)
        return next
      })
    }
  }

  const openEdit = (member: TeamMemberDto) => {
    setEditTarget(member)
    setEditFirstName(member.firstName ?? '')
    setEditLastName(member.lastName ?? '')
    setEditEmail(member.email ?? '')
    setEditFunctions([...member.functions])
  }

  const openInvite = (member: TeamMemberDto) => {
    setInviteTarget(member)
    setInviteEmail(member.email ?? '')
    setInviteRoleName(defaultRoleName)
  }

  const runConfirmAction = async () => {
    if (!confirmAction) return
    const { kind, member } = confirmAction
    try {
      setIsConfirming(true)
      if (kind === 'revoke-access' && member.userId) {
        await revokeTeamMemberAccess(member.userId)
        showSuccessToast('Accès retiré')
      } else if (kind === 'deactivate' && member.userId) {
        await deactivateTeamMember(member.userId)
        showSuccessToast('Employé désactivé')
      } else if (kind === 'delete' && member.userId) {
        await deleteTeamMember(member.userId)
        showSuccessToast('Employé supprimé')
      } else if (kind === 'revoke-invitation' && member.invitationId) {
        await revokeInvitation(member.invitationId)
        showSuccessToast('Invitation révoquée')
      }
      setConfirmAction(null)
      await reload()
    } catch (e) {
      toastApiError(e, "Erreur lors de l'action")
    } finally {
      setIsConfirming(false)
    }
  }

  const confirmCopy: Record<ConfirmAction['kind'], { title: string; body: (m: TeamMemberDto) => string; cta: string }> = {
    'revoke-access': {
      title: "Retirer l'accès",
      body: (m) =>
        `${m.displayName} ne pourra plus se connecter. Il reste dans l'équipe et dans le planning.`,
      cta: "Retirer l'accès",
    },
    deactivate: {
      title: 'Désactiver',
      body: (m) =>
        `${m.displayName} n'apparaîtra plus dans l'équipe ni dans le planning et ne pourra plus se connecter. Son historique est conservé. Vous pourrez le réactiver à tout moment.`,
      cta: 'Désactiver',
    },
    delete: {
      title: 'Supprimer définitivement',
      body: (m) => `${m.displayName} sera retiré définitivement de l'équipe.`,
      cta: 'Supprimer',
    },
    'revoke-invitation': {
      title: "Révoquer l'invitation",
      body: (m) =>
        `L'invitation envoyée à ${m.email ?? 'cette adresse'} sera annulée. ${m.userId ? "L'employé reste dans l'équipe." : ''}`,
      cta: 'Révoquer',
    },
  }

  // ── Crews (SOLY-PLAN-005) ──────────────────────────────────────────────────
  // The select is a SINGLE choice because a person belongs to one crew at most (V135). Picking
  // another crew moves them — the server does the move, so there is no window where the person is
  // in two crews or in none.
  const handleCrewChange = async (member: TeamMemberDto, value: string) => {
    if (!member.userId) return
    const userId = member.userId
    if (value === CREW_NEW) {
      setCrewTarget(member)
      setNewCrewName('')
      return
    }

    const current = crewByUser[userId]
    if (value === current) return
    // Two quick picks on one row would put two writes in flight, the second racing the first into
    // the unique index for a 409 nobody asked for.
    if (busyCrewFor.has(userId)) return

    setBusyCrewFor((busy) => new Set(busy).add(userId))
    try {
      if (value === CREW_NONE) {
        if (!current) return
        await unassignCrewMember(current, userId)
      } else {
        await assignCrewMember(value, userId)
      }
      const fresh = await getCrews()
      setCrews(fresh)
      setCrewsLoaded(true)

      // What the server holds may not be what was asked for: the other admin moved this person
      // between our read and our write, the server honoured its own state, and the select is about
      // to snap back. Saying so beats watching a choice undo itself in silence — the admin would
      // simply do it again.
      const landed = crewIdByUser(fresh)[userId]
      const wanted = value === CREW_NONE ? undefined : value
      if (landed !== wanted) {
        const landedName = fresh.find((c) => c.id === landed)?.name
        showErrorToast(
          landedName
            ? `${member.displayName || member.email} est dans « ${landedName} » — la page était périmée`
            : `${member.displayName || member.email} n'est plus dans une équipe — la page était périmée`,
        )
      }
    } catch (e) {
      toastApiError(e, "Impossible de changer l'équipe de ce membre")
      await reloadCrews()
    } finally {
      setBusyCrewFor((busy) => {
        const next = new Set(busy)
        next.delete(userId)
        return next
      })
    }
  }

  /**
   * Create a crew and put the member who asked for it straight in — one gesture, one result.
   *
   * Two calls, so the second can fail alone (a role revoked between them, a blip). When it does, the
   * crew EXISTS: saying « impossible de créer l'équipe » would send the user back to a
   * « + Nouvelle équipe… » that then answers « une équipe porte déjà ce nom », about a crew nowhere
   * on their screen. So the two failures are reported apart, the crew list is reloaded either way,
   * and the dialog closes either way — leaving it open with the name still filled invited exactly
   * that second attempt.
   */
  const handleCreateCrew = async () => {
    const name = newCrewName.trim()
    if (!name || !crewTarget?.userId) return
    setIsSavingCrew(true)
    let crew: CrewDto
    try {
      crew = await createCrew(name)
    } catch (e) {
      toastApiError(e, "Impossible de créer l'équipe")
      setIsSavingCrew(false)
      return
    }

    try {
      await assignCrewMember(crew.id, crewTarget.userId)
      showSuccessToast(`Équipe « ${crew.name} » créée`)
    } catch (e) {
      toastApiError(e, `Équipe « ${crew.name} » créée, mais le membre n'a pas pu y être placé`)
    } finally {
      setCrewTarget(null)
      setNewCrewName('')
      await reloadCrews()
      setIsSavingCrew(false)
    }
  }

  /**
   * Deleting a crew only ungroups: its members keep their events, their absences and their
   * functions, and reappear on their own agenda lane. That is the retreat path of this experiment,
   * so it stays one click behind one confirmation — no more.
   *
   * CREW_NOT_FOUND is treated as done, not as a failure: the server answers it for a crew that is
   * not ours OR no longer exists, and from the manage dialog the second case is what a second admin
   * produces. The outcome asked for holds either way, so the list reloads and nothing shouts.
   */
  const handleDeleteCrew = async () => {
    if (!crewToDelete) return
    try {
      setIsSavingCrew(true)
      await deleteCrew(crewToDelete.id)
      showSuccessToast(`Équipe « ${crewToDelete.name} » supprimée`)
    } catch (e) {
      if (extractErrorCode(e) === 'CREW_NOT_FOUND') {
        showSuccessToast(`Équipe « ${crewToDelete.name} » supprimée`)
      } else {
        toastApiError(e, "Impossible de supprimer l'équipe")
      }
    } finally {
      setCrewToDelete(null)
      await reloadCrews()
      setIsSavingCrew(false)
    }
  }

  const renderCrewCell = (m: TeamMemberDto) => {
    // A pending invitation has no user yet, so nothing to put in a crew.
    if (!m.userId) return <span className="text-xs text-muted-foreground">—</span>
    // The crew list never arrived: a functional select showing « Aucune équipe » would assert that
    // nobody is in a crew, which is not what we know. Say we do not know.
    if (!crewsLoaded) {
      return (
        <span className="text-xs text-muted-foreground" data-testid={`team-crew-unavailable-${m.userId}`}>
          indisponible
        </span>
      )
    }
    return (
      <Select
        value={crewByUser[m.userId] ?? CREW_NONE}
        disabled={busyCrewFor.has(m.userId)}
        onValueChange={(v) => handleCrewChange(m, v)}
      >
        <SelectTrigger className="h-8 w-[170px] text-xs" data-testid={`team-crew-select-${m.userId}`}>
          <SelectValue placeholder="Aucune équipe" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={CREW_NONE}>Aucune équipe</SelectItem>
          {orderedCrews.map((c) => (
            <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
          ))}
          <SelectItem value={CREW_NEW}>+ Nouvelle équipe…</SelectItem>
        </SelectContent>
      </Select>
    )
  }

  const renderFunctionBadges = (member: TeamMemberDto) => {
    if (!member.userId) return <span className="text-xs text-muted-foreground">—</span>
    return (
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="flex flex-wrap items-center gap-1 rounded-md px-1 py-0.5 hover:bg-accent text-left"
            data-testid="team-row-functions"
            aria-label={`Fonctions de ${member.displayName}`}
          >
            {member.functions.length === 0 ? (
              <span className="text-xs text-muted-foreground">Aucune fonction</span>
            ) : (
              member.functions.map((fn) => (
                <Badge key={fn} variant="secondary">
                  {functionLabel(fn)}
                </Badge>
              ))
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-56" align="start">
          <div className="space-y-2">
            <div className="text-sm font-medium">Fonctions</div>
            {ALL_FUNCTIONS.map((fn) => (
              <label key={fn} className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={member.functions.includes(fn)}
                  onCheckedChange={() => toggleFunction(member, fn)}
                  disabled={busyFunctions.has(`${member.userId}-${fn}`)}
                  data-testid={`team-function-toggle-${fn.toLowerCase()}`}
                />
                <span className="text-sm">{functionLabel(fn)}</span>
              </label>
            ))}
          </div>
        </PopoverContent>
      </Popover>
    )
  }

  const renderAccessCell = (member: TeamMemberDto) => {
    const badge = getAccessBadge(member.accessStatus)
    return (
      <div className="flex items-center gap-2">
        <Badge variant={badge.variant} data-testid="team-row-access-badge">
          {badge.label}
        </Badge>
        {member.accessStatus === 'active' && member.role ? (
          <span className="text-xs text-muted-foreground">{member.role.label}</span>
        ) : null}
        {member.accessStatus === 'none' && member.userId ? (
          <Button
            variant="outline"
            size="sm"
            onClick={() => openInvite(member)}
            disabled={rolesLoading || roles.length === 0}
            data-testid="team-invite-btn"
          >
            Inviter
          </Button>
        ) : null}
      </div>
    )
  }

  const renderRowActions = (member: TeamMemberDto) => {
    const isMember = member.userId !== null
    const status = member.accessStatus
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" data-testid="team-row-actions">
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {isMember ? (
            <DropdownMenuItem onClick={() => openEdit(member)} data-testid="team-edit-btn">
              Modifier
            </DropdownMenuItem>
          ) : null}
          {isMember && status === 'none' ? (
            <DropdownMenuItem onClick={() => openInvite(member)}>
              Inviter à se connecter
            </DropdownMenuItem>
          ) : null}
          {status === 'invited' ? (
            <>
              {isMember ? (
                <DropdownMenuItem onClick={() => openInvite(member)}>
                  Renvoyer l'invitation
                </DropdownMenuItem>
              ) : null}
              <DropdownMenuItem
                onClick={() => setConfirmAction({ kind: 'revoke-invitation', member })}
              >
                Révoquer l'invitation
              </DropdownMenuItem>
            </>
          ) : null}
          {isMember && status === 'active' ? (
            <>
              <DropdownMenuItem
                onClick={() => {
                  setRoleTarget(member)
                  setRoleName(member.role?.roleId ?? defaultRoleName)
                }}
              >
                Changer le rôle d'accès
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => setConfirmAction({ kind: 'revoke-access', member })}
                data-testid="team-revoke-access-btn"
              >
                Retirer l'accès
              </DropdownMenuItem>
            </>
          ) : null}
          {isMember ? (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setConfirmAction({ kind: 'deactivate', member })}>
                Désactiver
              </DropdownMenuItem>
              {status === 'none' || status === 'invited' ? (
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive"
                  onClick={() => setConfirmAction({ kind: 'delete', member })}
                  data-testid="team-remove-btn"
                >
                  Supprimer définitivement
                </DropdownMenuItem>
              ) : null}
            </>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>
    )
  }

  const functionCheckboxes = (
    selected: TeamFunction[],
    onChange: (next: TeamFunction[]) => void,
    idPrefix: string,
  ) => (
    <div className="flex flex-wrap gap-4">
      {ALL_FUNCTIONS.map((fn) => (
        <label key={fn} className="flex items-center gap-2 cursor-pointer">
          <Checkbox
            checked={selected.includes(fn)}
            onCheckedChange={(checked) => {
              onChange(
                checked === true ? [...selected, fn] : selected.filter((f) => f !== fn),
              )
            }}
            data-testid={`${idPrefix}-function-${fn.toLowerCase()}`}
          />
          <span className="text-sm">{functionLabel(fn)}</span>
        </label>
      ))}
    </div>
  )

  return (
    <>
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
          <div>
            <h2 className="text-base font-semibold">Équipe</h2>
            <p className="text-xs text-muted-foreground">
              Les personnes de votre équipe, leur fonction sur le terrain et leur accès Soly.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher par nom, email, fonction, rôle d'accès..."
              className="w-full sm:w-[340px]"
              data-testid="team-search-input"
            />
            <Button
              onClick={() => {
                setAddFirstName('')
                setAddLastName('')
                setAddEmail('')
                setAddFunctions([])
                setAddWithInvite(false)
                setAddRoleName(defaultRoleName)
                setAddOpen(true)
              }}
              disabled={isLoading}
              data-testid="team-add-btn"
            >
              Ajouter un employé
            </Button>
          </div>
        </div>

        <div className="border rounded-lg bg-background">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Employé</TableHead>
                {showFunctions ? <TableHead>Fonctions</TableHead> : null}
                {showCrews ? (
                  <TableHead>
                    <span className="inline-flex items-center gap-2">
                      Équipe
                      {crewsLoaded && crews.length > 0 ? (
                        <button
                          type="button"
                          onClick={() => setManageCrewsOpen(true)}
                          data-testid="team-crews-manage-btn"
                          className="text-[11px] font-normal text-muted-foreground underline-offset-2 hover:underline"
                        >
                          gérer
                        </button>
                      ) : null}
                    </span>
                  </TableHead>
                ) : null}
                <TableHead>Accès</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {active.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={2 + (showFunctions ? 1 : 0) + (showCrews ? 1 : 0) + 1}
                    className="text-center text-sm text-muted-foreground py-10"
                  >
                    {isLoading ? 'Chargement...' : 'Aucun résultat.'}
                  </TableCell>
                </TableRow>
              ) : (
                active.map((m) => (
                  <TableRow key={m.userId ?? m.invitationId ?? m.email} data-testid="team-row">
                    <TableCell>
                      <div className="space-y-0.5">
                        <div className="font-medium">{m.displayName || m.email || '—'}</div>
                        <div className="text-xs text-muted-foreground">{m.email ?? '—'}</div>
                      </div>
                    </TableCell>
                    {showFunctions ? <TableCell>{renderFunctionBadges(m)}</TableCell> : null}
                    {showCrews ? <TableCell>{renderCrewCell(m)}</TableCell> : null}
                    <TableCell>{renderAccessCell(m)}</TableCell>
                    <TableCell className="text-right">{renderRowActions(m)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {inactive.length > 0 ? (
          <Collapsible open={inactiveOpen} onOpenChange={setInactiveOpen}>
            <CollapsibleTrigger asChild>
              <button
                type="button"
                className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
                data-testid="team-inactive-toggle"
              >
                {inactiveOpen ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
                Inactifs ({inactive.length})
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-2">
              <div className="border rounded-lg bg-background">
                <Table>
                  <TableBody>
                    {inactive.map((m) => (
                      <TableRow key={m.userId} className="opacity-60" data-testid="team-inactive-row">
                        <TableCell>
                          <div className="space-y-0.5">
                            <div className="font-medium">{m.displayName}</div>
                            <div className="text-xs text-muted-foreground">
                              {formatDeactivatedAt(m.deactivatedAt)}
                            </div>
                          </div>
                        </TableCell>
                        {showFunctions ? (
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              {m.functions.map((fn) => (
                                <Badge key={fn} variant="outline">
                                  {functionLabel(fn)}
                                </Badge>
                              ))}
                            </div>
                          </TableCell>
                        ) : null}
                        {/* Deactivating someone does not take them out of their crew, so the exit
                            has to exist here too — otherwise the only way out was deleting the whole
                            crew. */}
                        {showCrews ? <TableCell>{renderCrewCell(m)}</TableCell> : null}
                        <TableCell className="text-right">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={async () => {
                              if (!m.userId) return
                              try {
                                await reactivateTeamMember(m.userId)
                                showSuccessToast('Employé réactivé')
                                await reload()
                              } catch (e) {
                                toastApiError(e, 'Erreur lors de la réactivation')
                              }
                            }}
                            data-testid="team-reactivate-btn"
                          >
                            Réactiver
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CollapsibleContent>
          </Collapsible>
        ) : null}
      </div>

      {/* Add member dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent data-testid="team-add-dialog">
          <DialogHeader>
            <DialogTitle>Ajouter un employé</DialogTitle>
            <DialogDescription>
              Un employé fait partie de l'équipe — lui donner un accès Soly est optionnel.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <div className="text-sm font-medium">Prénom</div>
                <Input
                  value={addFirstName}
                  onChange={(e) => setAddFirstName(e.target.value)}
                  placeholder="Prénom"
                  data-testid="team-add-firstname"
                />
              </div>
              <div className="space-y-2">
                <div className="text-sm font-medium">Nom</div>
                <Input
                  value={addLastName}
                  onChange={(e) => setAddLastName(e.target.value)}
                  placeholder="Nom de famille"
                  data-testid="team-add-lastname"
                />
              </div>
            </div>

            {showFunctions ? (
              <div className="space-y-2">
                <div className="text-sm font-medium">Fonctions</div>
                {functionCheckboxes(addFunctions, setAddFunctions, 'team-add')}
              </div>
            ) : null}

            <div className="space-y-2">
              <div className="text-sm font-medium">
                Email {addWithInvite ? '' : '(optionnel)'}
              </div>
              <Input
                value={addEmail}
                onChange={(e) => setAddEmail(e.target.value)}
                placeholder="email@exemple.com"
                type="email"
                data-testid="team-add-email"
              />
            </div>

            <div className="space-y-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={addWithInvite}
                  onCheckedChange={(checked) => setAddWithInvite(checked === true)}
                  data-testid="team-add-invite-toggle"
                />
                <span className="text-sm font-medium">Envoyer une invitation à se connecter</span>
              </label>
              {!addWithInvite ? (
                <p className="text-xs text-muted-foreground">
                  Vous pourrez l'inviter plus tard. Sans accès, il apparaît dans le planning mais
                  ne peut pas se connecter.
                </p>
              ) : (
                <div className="space-y-2 pt-1">
                  <div className="text-sm font-medium">Rôle d'accès</div>
                  <Select value={addRoleName} onValueChange={setAddRoleName}>
                    <SelectTrigger className="w-full" data-testid="team-add-role-select">
                      <SelectValue placeholder="Sélectionner un rôle d'accès" />
                    </SelectTrigger>
                    <SelectContent>
                      {roles.map((r) => (
                        <SelectItem key={r.id} value={r.id}>
                          {r.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setAddOpen(false)}
              disabled={isAdding}
            >
              Annuler
            </Button>
            <Button
              type="button"
              onClick={async () => {
                const firstName = addFirstName.trim()
                const lastName = addLastName.trim()
                const email = addEmail.trim()
                if (!firstName || !lastName) {
                  showErrorToast('Prénom et nom sont requis')
                  return
                }
                if (addWithInvite && !email) {
                  showErrorToast("L'email est requis pour envoyer une invitation")
                  return
                }
                if (addWithInvite && !addRoleName) {
                  showErrorToast("Sélectionnez un rôle d'accès")
                  return
                }
                if (email && !EMAIL_REGEX.test(email)) {
                  showErrorToast('Email invalide')
                  return
                }

                try {
                  setIsAdding(true)
                  const created = await createTeamMember({
                    firstName,
                    lastName,
                    email: email || null,
                    functions: addFunctions,
                  })
                  if (addWithInvite && created.userId) {
                    // The member exists from here on: an invitation failure is
                    // a PARTIAL success — close and show the real state, the
                    // row offers Inviter to retry. Leaving the dialog open
                    // would hide the created member and make a second submit
                    // fail with a baffling EMAIL_TAKEN.
                    try {
                      await createInvitation({
                        email,
                        firstname: firstName,
                        lastname: lastName,
                        roleName: addRoleName,
                        userId: created.userId,
                      })
                      showSuccessToast('Employé ajouté et invitation envoyée')
                    } catch (e) {
                      console.error(e)
                      const code = extractErrorCode(e)
                      const reason = code && ERROR_MESSAGES[code]
                      showErrorToast(
                        `Employé ajouté, mais l'invitation n'est pas partie${reason ? ` : ${reason.toLowerCase()}` : ' — réessayez depuis sa ligne'}`,
                      )
                    }
                  } else {
                    showSuccessToast('Employé ajouté')
                  }
                  setAddOpen(false)
                  await reload()
                } catch (e) {
                  toastApiError(e, "Erreur lors de l'ajout de l'employé")
                } finally {
                  setIsAdding(false)
                }
              }}
              disabled={isAdding}
              data-testid="team-add-submit"
            >
              {isAdding ? 'Ajout...' : 'Ajouter'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit member dialog */}
      <Dialog open={editTarget !== null} onOpenChange={(open) => !open && setEditTarget(null)}>
        <DialogContent data-testid="team-edit-dialog">
          <DialogHeader>
            <DialogTitle>Modifier l'employé</DialogTitle>
            <DialogDescription>Nom, fonctions{editTarget?.accessStatus === 'none' ? ' et email' : ''}.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <div className="text-sm font-medium">Prénom</div>
                <Input
                  value={editFirstName}
                  onChange={(e) => setEditFirstName(e.target.value)}
                  data-testid="team-edit-firstname"
                />
              </div>
              <div className="space-y-2">
                <div className="text-sm font-medium">Nom</div>
                <Input
                  value={editLastName}
                  onChange={(e) => setEditLastName(e.target.value)}
                  data-testid="team-edit-lastname"
                />
              </div>
            </div>

            {showFunctions ? (
              <div className="space-y-2">
                <div className="text-sm font-medium">Fonctions</div>
                {functionCheckboxes(editFunctions, setEditFunctions, 'team-edit')}
              </div>
            ) : null}

            <div className="space-y-2">
              <div className="text-sm font-medium">Email</div>
              <Input
                value={editEmail}
                onChange={(e) => setEditEmail(e.target.value)}
                type="email"
                disabled={editTarget?.accessStatus !== 'none'}
                data-testid="team-edit-email"
              />
              {editTarget?.accessStatus !== 'none' ? (
                <p className="text-xs text-muted-foreground">
                  L'email d'un membre avec accès ou invité se gère via son compte.
                </p>
              ) : null}
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setEditTarget(null)}
              disabled={isEditing}
            >
              Annuler
            </Button>
            <Button
              type="button"
              onClick={async () => {
                if (!editTarget?.userId) return
                const firstName = editFirstName.trim()
                const lastName = editLastName.trim()
                if (!firstName || !lastName) {
                  showErrorToast('Prénom et nom sont requis')
                  return
                }
                const newEmail = editEmail.trim()
                if (editTarget.accessStatus === 'none' && editTarget.email && newEmail === '') {
                  // The BE cannot clear an email — a silent no-op success would lie.
                  showErrorToast("L'email ne peut pas être effacé — remplacez-le ou laissez-le")
                  return
                }
                const emailChanged =
                  editTarget.accessStatus === 'none' &&
                  newEmail !== '' &&
                  newEmail.toLowerCase() !== (editTarget.email ?? '').toLowerCase()
                if (emailChanged && !EMAIL_REGEX.test(newEmail)) {
                  showErrorToast('Email invalide')
                  return
                }

                try {
                  setIsEditing(true)
                  await updateTeamMember(editTarget.userId, {
                    firstName,
                    lastName,
                    email: emailChanged ? newEmail : null,
                    functions: editFunctions,
                  })
                  showSuccessToast('Employé modifié')
                  setEditTarget(null)
                  await reload()
                } catch (e) {
                  toastApiError(e, 'Erreur lors de la modification')
                } finally {
                  setIsEditing(false)
                }
              }}
              disabled={isEditing}
              data-testid="team-edit-submit"
            >
              {isEditing ? 'Modification...' : 'Enregistrer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invite dialog (deferred invitation on an existing member) */}
      <Dialog open={inviteTarget !== null} onOpenChange={(open) => !open && setInviteTarget(null)}>
        <DialogContent data-testid="team-invite-dialog">
          <DialogHeader>
            <DialogTitle>Inviter à se connecter</DialogTitle>
            <DialogDescription>
              {inviteTarget
                ? `${inviteTarget.displayName} recevra un email pour activer son accès Soly.`
                : ''}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <div className="text-sm font-medium">Employé</div>
              <div className="text-sm text-muted-foreground">{inviteTarget?.displayName}</div>
            </div>
            <div className="space-y-2">
              <div className="text-sm font-medium">Email</div>
              <Input
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="email@exemple.com"
                type="email"
                data-testid="team-invite-email"
              />
            </div>
            <div className="space-y-2">
              <div className="text-sm font-medium">Rôle d'accès</div>
              <Select value={inviteRoleName} onValueChange={setInviteRoleName}>
                <SelectTrigger className="w-full" data-testid="team-invite-role-select">
                  <SelectValue placeholder="Sélectionner un rôle d'accès" />
                </SelectTrigger>
                <SelectContent>
                  {roles.map((r) => (
                    <SelectItem key={r.id} value={r.id}>
                      {r.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setInviteTarget(null)}
              disabled={isInviting}
            >
              Annuler
            </Button>
            <Button
              type="button"
              onClick={async () => {
                if (!inviteTarget?.userId) return
                const email = inviteEmail.trim()
                if (!email || !EMAIL_REGEX.test(email)) {
                  showErrorToast('Email invalide')
                  return
                }
                if (!inviteRoleName) {
                  showErrorToast("Sélectionnez un rôle d'accès")
                  return
                }
                try {
                  setIsInviting(true)
                  // Resend/correction: the BE returns the EXISTING pending
                  // invitation as-is (no new email, edits ignored) — revoke it
                  // first so the new one truly goes out.
                  if (inviteTarget.invitationId) {
                    await revokeInvitation(inviteTarget.invitationId)
                  }
                  await createInvitation({
                    email,
                    firstname: inviteTarget.firstName ?? inviteTarget.displayName,
                    lastname: inviteTarget.lastName ?? undefined,
                    roleName: inviteRoleName,
                    userId: inviteTarget.userId,
                  })
                  showSuccessToast('Invitation envoyée')
                  setInviteTarget(null)
                  await reload()
                } catch (e) {
                  toastApiError(e, "Erreur lors de l'envoi de l'invitation")
                  // The revoke may have gone through: reflect the real state.
                  setInviteTarget(null)
                  await reload()
                } finally {
                  setIsInviting(false)
                }
              }}
              disabled={isInviting}
              data-testid="team-invite-submit"
            >
              {isInviting ? 'Envoi...' : 'Envoyer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change access role dialog */}
      <Dialog open={roleTarget !== null} onOpenChange={(open) => !open && setRoleTarget(null)}>
        <DialogContent data-testid="team-role-dialog">
          <DialogHeader>
            <DialogTitle>Changer le rôle d'accès</DialogTitle>
            <DialogDescription>{roleTarget?.displayName}</DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            <div className="text-sm font-medium">Rôle d'accès</div>
            <Select value={roleName} onValueChange={setRoleName}>
              <SelectTrigger className="w-full" data-testid="team-role-select">
                <SelectValue placeholder="Sélectionner un rôle d'accès" />
              </SelectTrigger>
              <SelectContent>
                {roles.map((r) => (
                  <SelectItem key={r.id} value={r.id}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setRoleTarget(null)}
              disabled={isChangingRole}
            >
              Annuler
            </Button>
            <Button
              type="button"
              onClick={async () => {
                if (!roleTarget?.userId || !roleName) return
                try {
                  setIsChangingRole(true)
                  await changeUserRole(roleTarget.userId, { roleName })
                  showSuccessToast("Rôle d'accès modifié")
                  setRoleTarget(null)
                  await reload()
                } catch (e) {
                  toastApiError(e, "Erreur lors de la modification du rôle d'accès")
                } finally {
                  setIsChangingRole(false)
                }
              }}
              disabled={isChangingRole}
            >
              {isChangingRole ? 'Modification...' : 'Enregistrer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Shared confirmation dialog */}
      <Dialog
        open={confirmAction !== null}
        onOpenChange={(open) => !open && setConfirmAction(null)}
      >
        <DialogContent data-testid="team-confirm-dialog">
          {confirmAction ? (
            <>
              <DialogHeader>
                <DialogTitle>{confirmCopy[confirmAction.kind].title}</DialogTitle>
                <DialogDescription>
                  {confirmCopy[confirmAction.kind].body(confirmAction.member)}
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setConfirmAction(null)}
                  disabled={isConfirming}
                >
                  Annuler
                </Button>
                <Button
                  type="button"
                  variant={confirmAction.kind === 'delete' ? 'destructive' : 'default'}
                  onClick={runConfirmAction}
                  disabled={isConfirming}
                  data-testid="team-confirm-submit"
                >
                  {isConfirming ? 'En cours...' : confirmCopy[confirmAction.kind].cta}
                </Button>
              </DialogFooter>
            </>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Create a crew — opened from « + Nouvelle équipe… » on a member's row (SOLY-PLAN-005). */}
      <Dialog
        open={crewTarget !== null}
        onOpenChange={(open) => {
          if (!open) {
            setCrewTarget(null)
            setNewCrewName('')
          }
        }}
      >
        <DialogContent data-testid="team-crew-create-dialog">
          <DialogHeader>
            <DialogTitle>Nouvelle équipe</DialogTitle>
            <DialogDescription>
              {crewTarget
                ? `${crewTarget.displayName || crewTarget.email} y sera placé. Dans l'agenda, les membres d'une équipe partagent une seule ligne.`
                : null}
            </DialogDescription>
          </DialogHeader>
          <Input
            value={newCrewName}
            onChange={(e) => setNewCrewName(e.target.value)}
            placeholder="Équipe Nord"
            maxLength={CREW_NAME_MAX}
            data-testid="team-crew-name-input"
          />
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setCrewTarget(null)}
              disabled={isSavingCrew}
            >
              Annuler
            </Button>
            <Button
              type="button"
              onClick={handleCreateCrew}
              disabled={isSavingCrew || newCrewName.trim().length === 0}
              data-testid="team-crew-create-submit"
            >
              {isSavingCrew ? 'En cours...' : 'Créer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manage crews: the list and, above all, the way out of the experiment. */}
      <Dialog open={manageCrewsOpen} onOpenChange={setManageCrewsOpen}>
        <DialogContent data-testid="team-crews-manage-dialog">
          <DialogHeader>
            <DialogTitle>Équipes</DialogTitle>
            <DialogDescription>
              Supprimer une équipe ne fait que dégrouper : ses membres retrouvent leur propre ligne
              dans l'agenda, et rien d'autre ne change.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {orderedCrews.length === 0 ? (
              <p className="text-sm text-muted-foreground">Aucune équipe.</p>
            ) : (
              orderedCrews.map((c) => (
                <div
                  key={c.id}
                  className="flex items-center justify-between rounded-md border px-3 py-2"
                  data-testid={`team-crew-row-${c.id}`}
                >
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-medium">{c.name}</span>
                    <span className="text-xs text-muted-foreground">
                      {c.memberUserIds.length === 0
                        ? 'aucun membre'
                        : `${c.memberUserIds.length} membre${c.memberUserIds.length > 1 ? 's' : ''}`}
                    </span>
                  </span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setCrewToDelete(c)}
                    disabled={isSavingCrew}
                    data-testid={`team-crew-delete-${c.id}`}
                  >
                    Supprimer
                  </Button>
                </div>
              ))
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setManageCrewsOpen(false)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation, kept apart from the shared one: its consequence is not a person's. */}
      <Dialog open={crewToDelete !== null} onOpenChange={(open) => !open && setCrewToDelete(null)}>
        <DialogContent data-testid="team-crew-delete-dialog">
          <DialogHeader>
            <DialogTitle>Supprimer cette équipe ?</DialogTitle>
            <DialogDescription>
              {crewToDelete
                ? `« ${crewToDelete.name} » disparaît de l'agenda. Ses membres gardent leurs chantiers, leurs absences et leurs fonctions.`
                : null}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setCrewToDelete(null)}
              disabled={isSavingCrew}
            >
              Annuler
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleDeleteCrew}
              disabled={isSavingCrew}
              data-testid="team-crew-delete-submit"
            >
              {isSavingCrew ? 'En cours...' : 'Supprimer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
